import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    IconButton,
    Tooltip,
    Box
} from "@mui/material/";
import actions from "../../Store/actions";
import { format } from "../../common/lib";
import { strings } from "../../common/i18n";
import { AuditGroupForm } from "../../common/components";
import EditIcon from "@mui/icons-material/Edit";
import EditAuditGroup from "./EditAuditGroup";
import MatReactTable from "../../common/components/MaterialReactTable";
import { dateRangeFilterFn } from "../../common/components/MaterialReactTable/filterFns";
import AddIcon from "@mui/icons-material/Add";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";

import styles from "../styles";

/**
 * Audit Groups Component
 */
export default () => {
    const { auditGroups } = useSelector((state) => state);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentAuditGroup, setCurrentAuditGroup] = useState(null);
    const [type, setType] = useState("");
    const dispatch = useDispatch();

    /** Rows data - Proper null date handling */
    const data = useMemo(() => {
        return auditGroups.data
            .map((item) => ({
                ...item,
                startDate: item.startDate ? new Date(item.startDate) : null,
                endDate: item.endDate ? new Date(item.endDate) : null,
                closedDate: item.closedDate ? new Date(item.closedDate) : null,
                startDateFormatted: item.startDate
                    ? format.dateTimeNew(item.startDate)
                    : "-",
                endDateFormatted: item.endDate
                    ? format.dateTimeNew(item.endDate)
                    : "-",
                closedDateFormatted: item.closedDate
                    ? format.dateTimeNew(item.closedDate)
                    : "-"
            }))
            .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
    }, [auditGroups.data]);

    const onAddGroup = () => {
        setType("addAuditGroup");
        setDialogOpen(true);
    };

    const onRowActions = async (row, msg) => {
        setType(msg);
        setDialogOpen(true);
        setCurrentAuditGroup(row);
        await dispatch(actions.auditGroups.create.itemSet(row));
    };

    const onDialogClose = () => setDialogOpen(false);

    const columns = useMemo(
        () => [
            {
                id: "name",
                header: strings("name"),
                accessorKey: "name",
                filterVariant: "autocomplete",
                size: 150
            },
            {
                id: "startDate",
                header: strings("startDate"),
                accessorKey: "startDate",
                filterVariant: "date-range",
                Cell: ({ cell }) => cell.row.original.startDateFormatted,
                sortingFn: "datetime",
                filterFn: dateRangeFilterFn, // Reuse the common function
                size: 150
            },
            {
                id: "endDate",
                header: strings("endDate"),
                accessorKey: "endDate",
                filterVariant: "date-range",
                Cell: ({ cell }) => cell.row.original.endDateFormatted,
                sortingFn: "datetime",
                filterFn: dateRangeFilterFn, // Reuse the common function
                size: 150
            },
            {
                id: "closedDate",
                header: strings("closedDate"),
                accessorKey: "closedDate",
                filterVariant: "date-range",
                Cell: ({ cell }) => cell.row.original.closedDateFormatted,
                sortingFn: "datetime",
                filterFn: dateRangeFilterFn, // Reuse the common function
                size: 150
            }
        ],
        []
    );

    /** Row Actions */
    const rowActions = (row) => (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}>
            <IconButton
                size="small"
                onClick={() => onRowActions(row.original, "editAuditGroup")}
            >
                <Tooltip title={strings("edit")}>
                    <EditIcon fontSize="small" color="primary" />
                </Tooltip>
            </IconButton>
            {row.original.closedDate === null ? (
                <IconButton
                    size="small"
                    onClick={() => onRowActions(row.original, "closeAuditGroup")}
                >
                    <Tooltip title={strings("close")}>
                        <CloseSharpIcon fontSize="small" color="primary" />
                    </Tooltip>
                </IconButton>
            ) : (
                <Box sx={{ width: 30 }} />
            )}
        </Box>
    );

    /** Toolbar components for search  */
    const toolBarComponents = () => (
        <Button
            sx={styles.primaryButton}
            variant="contained"
            size="small"
            color="primary"
            onClick={onAddGroup}
            endIcon={<AddIcon />}
        >
            {strings("addGroup")}
        </Button>
    );

    return (
        <>
            <MatReactTable
                persistKey="manageGroups"
                columns={columns}
                data={data}
                loading={false}
                rowActions={rowActions}
                toolBarComponents={toolBarComponents}
                noRowsMessage={strings("messages.noAssets")}
                initialState={{
                    sorting: [{ id: "startDate", desc: true }]
                }}
            />
            <Dialog onClose={onDialogClose} open={dialogOpen}>
                <DialogTitle>{strings(type)}</DialogTitle>
                <DialogContent dividers>
                    {type === "addAuditGroup" ? (
                        <AuditGroupForm onSubmit={onDialogClose} />
                    ) : (
                        <EditAuditGroup
                            type={type}
                            onCancel={onDialogClose}
                            auditGroup={currentAuditGroup}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
